<script setup lang="ts">
import { useSortByName } from '@/common/composables/sortByName';
import { useRefereshProductStore } from '@/products/commands/refreshProductStore';
import { useRefreshStorageAreaStore } from '@/storageAreas/stores/refreshStorageAreaStore';
import { storeToRefs } from 'pinia';
import { DataTable, Column, Button, InputText, Popover } from 'primevue';
import type { StorageArea } from 'shared/storageAreas/domain/storageArea';
import { useStorageAreaStore } from 'shared/storageAreas/stores/storageAreaStore';
import { useProductStore } from 'shared/products/stores/productStore';
import { useTranslations } from 'shared/translations/composables/useTranslations';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { getDaysSinceDate } from 'shared/common/date';
import { computed, onMounted, ref } from 'vue';
import { isBulkStorage, isLogYard } from 'shared/storageAreas/domain/storageAreaType';
const storageAreaStore = useStorageAreaStore();
const storageAreas = useSortByName({
  list: storeToRefs(storageAreaStore).storageAreas,
  getName: (s) => s.name,
});
const translations = useTranslations();
const languageStore = useLanguageStore();
const productStore = useProductStore();
const refreshProductStore = useRefereshProductStore();
const refreshStorageAreaStore = useRefreshStorageAreaStore();
const expandedRows = ref([]); //håller reda på vilka rader som är expanderade
const rowsPerPage = ref(50);

const loading = computed(
  () => refreshStorageAreaStore.loading || refreshProductStore.loading
);

//popover för produkter
const popover = ref();
const selectedStorageArea = ref<StorageArea | null>(null);

function togglePopover(event: Event, storageArea: StorageArea) {
  selectedStorageArea.value = storageArea;
  popover.value.toggle(event);
}

onMounted(() => {
  void Promise.all([
    refreshStorageAreaStore.initialize(),
    refreshProductStore.initialize(),
  ]);
});

const filters = ref({
  global: {
    value: null,
    matchMode: 'contains',
  },
});

// Hämtar alla produkter som finns kopplade lagringsområde och returnerar ett objekt med array och lista
function getProductsFromStorageArea(storageArea: StorageArea): {
  productInfo: ProductInfo[];
  productNames: string;
} {
  //skapar en tom lista
  let productInfo: ProductInfo[] = [];

  //inget data = tom lista
  if (!storageArea.logYardData) {
    return { productInfo: [], productNames: '' };
  }

  //loopa igenom innehållet i lagerplatserna
  for (const content of storageArea.logYardData.content) {
    const product = productStore.getById(content.productId);
    //lägger till värden till produktinfo-objektet
    if (product) {
      productInfo.push({
        id: product.id,
        productName: product.name,
        numberOfLogs: content.numberOfLogs,
        volume: content.volume,
      });
    }
  }
  //sorterar resultatet i volymordning
  productInfo.sort((a, b) => {
    return b.volume - a.volume;
  });

  //gör objektet till en kommaseparerad lista
  const productList = productInfo.map((p) => p.productName).join(', ');

  //returnerar ett objekt med objekt och lista
  return {
    productInfo: productInfo,
    productNames: productList,
  };
}

function getNumberOfLogs(storageArea: StorageArea) {
  let count = 0;
  if (!storageArea.logYardData) return '';
  for (const content of storageArea.logYardData.content) {
    count += content.numberOfLogs;
  }
  return count.toString();
}

function getVolume(storageArea: StorageArea) {
  let volume = 0;
  if (!storageArea.logYardData) return '';
  for (const content of storageArea.logYardData.content) {
    volume += content.volume;
  }
  return volume.toString();
}

function getFirstMoveTo(storageArea: StorageArea) {
  switch (true) {
    case isLogYard(storageArea.type):
      return storageArea.logYardData?.firstMoveToUtc
        ? languageStore.formatDate(storageArea.logYardData.firstMoveToUtc)
        : '';

    case isBulkStorage(storageArea.type):
      return storageArea.bulkStorageData?.firstMoveToUtc
        ? languageStore.formatDate(storageArea.bulkStorageData.firstMoveToUtc)
        : '';

    default:
      return '';
  }
}

function getDaysSinceFirstMove(storageArea: StorageArea) {
  switch (true) {
    case isLogYard(storageArea.type):
      return storageArea.logYardData?.firstMoveToUtc
        ? getDaysSinceDate(storageArea.logYardData.firstMoveToUtc)
        : '';

    case isBulkStorage(storageArea.type):
      return storageArea.bulkStorageData?.firstMoveToUtc
        ? getDaysSinceDate(storageArea.bulkStorageData.firstMoveToUtc)
        : '';

    default:
      return '';
  }
}


//beskriver strukturen för ProduktInfo-objektet
interface ProductInfo {
  id: string;
  productName: string;
  numberOfLogs: number;
  volume: number;
}
</script>

<template>
  <DataTable class="storage-area-table" :value="storageAreas" data-key="id" sort-field="name" paginator
    v-model:rows="rowsPerPage" :rowsPerPageOptions="[25, 50, 100, 250]" scrollable :loading="loading"
    scrollHeight="flex" width="auto" v-model:filters="filters" v-model:expandedRows="expandedRows">
    <template #header>
      <div class="flex flex-wrap items-center justify-between gap-2">
        <span class="text-xl font-bold">{{ translations.generic.storageAreas }}</span>
        <InputText class="search-field" autofocus v-model="filters.global.value"
          :placeholder="translations.generic.search" />
        <Button class="refresh-button" icon="pi pi-refresh" :loading="loading" rounded raised
          @click="refreshStorageAreaStore.refresh()"></Button>
      </div>
    </template>
    <template #empty>{{ translations.storageAreas.notFound }}</template>
    <template #loading>{{ translations.generic.loadingTableData }}</template>
    <Column field="name" :header="translations.generic.name" sortable></Column>
    <Column :header="translations.generic.products" style="width: 1rem">
      <template #body="{ data }">
        <p class="truncate w-45">{{ getProductsFromStorageArea(data).productNames }}</p>
      </template>
    </Column>
    <Column>
      <template #body="{ data }">
        <Button icon="pi pi-info-circle" rounded text @click="togglePopover($event, data)"></Button>
      </template>
    </Column>
    <Popover ref="popover">
      <DataTable :value="getProductsFromStorageArea(selectedStorageArea!).productInfo">
        <Column field="productName" :header="translations.generic.products"></Column>
        <Column field="numberOfLogs" :header="translations.generic.numberOfLogs"></Column>
        <Column field="volume" :header="translations.generic.volume"></Column>
      </DataTable>
    </Popover>
    <Column :field="getNumberOfLogs" :header="translations.generic.numberOfLogs" sortable></Column>
    <Column :field="getVolume" :header="translations.generic.volume" sortable></Column>
    <Column :field="getFirstMoveTo" :header="translations.storageAreas.storageAreaTable.firstMoveTo" sortable></Column>
    <Column :field="getDaysSinceFirstMove" :header="translations.storageAreas.storageAreaTable.daysSinceFirstMove"
      sortable>
    </Column>
  </DataTable>
</template>

<style scoped>
.product-card {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  border: 1px solid var(--p-surface-300);
  min-width: 8rem;
}

.storage-area-container {
  height: 100%;
  background-color: var(--p-surface-100);
  overflow-y: auto;
}

:deep(.p-datatable-header div) {
  display: grid;
  grid-template-columns: auto 1fr auto auto;
}

.refresh-button {
  grid-column: 4;
}

.search-field {
  grid-column: 3;
}
</style>
